import { useEffect, useMemo, useState } from "react";
import { Activity, CalendarDays, LayoutGrid } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";

const TYPES = [
  { type: "T0", label: "T0 · Studio" },
  { type: "T1", label: "T1" },
  { type: "T2", label: "T2" },
  { type: "T3", label: "T3" },
  { type: "T4", label: "T4" },
  { type: "VS", label: "Villa pequena" },
  { type: "VM", label: "Villa média" },
  { type: "VL", label: "Villa grande" },
];

const WD = ["Seg", "Ter", "Qua", "Qui", "Sex", "Sáb", "Dom"];

function todayIso() {
  const d = new Date(); const pad = n => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

// Monday of the week containing `iso`
function weekStart(iso) {
  const d = new Date(`${iso}T12:00:00`);
  const day = (d.getDay() + 6) % 7; // Mon=0
  d.setDate(d.getDate() - day);
  const pad = n => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
function addDays(iso, n) {
  const d = new Date(`${iso}T12:00:00`); d.setDate(d.getDate() + n);
  const pad = x => String(x).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function loadColor(pct) {
  if (pct === 0)   return { bg: "#0a3a33", ring: "#10b981", label: "Livre" };
  if (pct <= 25)   return { bg: "#0a3a33", ring: "#10b981", label: "Maioria livre" };
  if (pct <= 50)   return { bg: "#0a3a33", ring: "#d4af37", label: "Ocupação média" };
  if (pct <= 75)   return { bg: "#3a2a15", ring: "#d4af37", label: "Pressão" };
  if (pct < 100)   return { bg: "#3a1a15", ring: "#ef4444", label: "Quase cheio" };
  return               { bg: "#3a1a15", ring: "#ef4444", label: "Esgotado" };
}

export default function AdminCapacity() {
  const [tab, setTab] = useState("day"); // "day" | "week"
  const [date, setDate] = useState(todayIso);
  const [rows, setRows] = useState({});
  const [week, setWeek] = useState(null);
  const [loading, setLoading] = useState(false);

  async function loadDay() {
    setLoading(true);
    try {
      const results = await Promise.all(
        TYPES.map(t => api.get("/admin/capacity-heatmap", {
          params: { date, apt_type: t.type, start_hour: 7, end_hour: 21 },
        }).then(({ data }) => [t.type, data]))
      );
      setRows(Object.fromEntries(results));
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }

  async function loadWeek() {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/capacity-week", {
        params: { start_date: weekStart(date), slot_time: "10:00" },
      });
      setWeek(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }

  useEffect(() => {
    if (tab === "day") loadDay();
    else loadWeek();
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [date, tab]);

  const totalEmps = rows[TYPES[0]?.type]?.total_employees
                    || (week?.days?.[0]?.by_type?.T0?.total ?? 0);
  const hours = useMemo(() => {
    const first = rows[TYPES[0]?.type];
    return first?.slots?.map(s => s.time) || [];
  }, [rows]);

  return (
    <div className="space-y-8">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="eyebrow mb-3">Operações</div>
          <h1 className="font-display text-5xl">Gestor de Capacidade</h1>
          <div className="text-[#a0b2af] text-xs mt-2 uppercase tracking-widest">
            {tab === "day"
              ? "Mapa de ocupação hora-a-hora por tipologia"
              : "Resumo semanal · slot 10:00 por tipologia"} · {totalEmps} colaboradores ativos
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="inline-flex border border-[#1a4640] rounded-sm overflow-hidden">
            <button type="button" onClick={() => setTab("day")}
                    className={`px-3 py-2 text-xs uppercase tracking-widest inline-flex items-center gap-2 ${tab === "day" ? "bg-[#d4af37] text-[#041f1b]" : "text-[#a0b2af]"}`}
                    data-testid="tab-day">
              <LayoutGrid size={13} /> Dia
            </button>
            <button type="button" onClick={() => setTab("week")}
                    className={`px-3 py-2 text-xs uppercase tracking-widest inline-flex items-center gap-2 ${tab === "week" ? "bg-[#d4af37] text-[#041f1b]" : "text-[#a0b2af]"}`}
                    data-testid="tab-week">
              <CalendarDays size={13} /> Semana
            </button>
          </div>
          <input type="date" value={date} onChange={e => setDate(e.target.value)}
                 className="bg-[#062c27] border border-[#1a4640] rounded-sm px-4 py-2 text-sm"
                 data-testid="capacity-date" />
          <button onClick={() => setDate(todayIso())} className="btn-ghost-gold px-3 py-2 text-xs uppercase tracking-widest" data-testid="capacity-today">
            Hoje
          </button>
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-5 text-xs">
        {[
          { pct: 0,   label: "Livre" },
          { pct: 40,  label: "Ocupação média" },
          { pct: 70,  label: "Pressão" },
          { pct: 100, label: "Esgotado" },
        ].map(l => {
          const c = loadColor(l.pct);
          return (
            <div key={l.label} className="flex items-center gap-2">
              <span className="inline-block w-4 h-4 rounded-sm" style={{ background: c.bg, border: `1.5px solid ${c.ring}` }} />
              <span className="text-[#a0b2af]">{l.label}</span>
            </div>
          );
        })}
      </div>

      {tab === "day" ? (
        <div className="overflow-x-auto lux-card p-4" data-testid="capacity-heatmap">
          <table className="min-w-full">
            <thead>
              <tr>
                <th className="text-left text-[10px] uppercase tracking-widest text-[#a0b2af] pb-3 pr-3 sticky left-0 bg-[#062c27]">Tipologia</th>
                {hours.map(h => (
                  <th key={h} className="text-[10px] font-mono text-[#a0b2af] pb-3 px-1 text-center">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-[#1a4640]">
              {TYPES.map(t => {
                const data = rows[t.type];
                return (
                  <tr key={t.type} className="hover:bg-[#0a3a33]/30" data-testid={`row-${t.type}`}>
                    <td className="py-2 pr-3 sticky left-0 bg-[#062c27]">
                      <div className="font-display text-base">{t.type}</div>
                      <div className="text-[10px] text-[#a0b2af] truncate max-w-[7rem]">{t.label}</div>
                    </td>
                    {(data?.slots || []).map(s => {
                      const c = loadColor(s.load_pct);
                      return (
                        <td key={s.time} className="p-0.5">
                          <div title={`${s.time} · ${s.available}/${s.total} livres · ${c.label}`}
                               className="min-w-[44px] h-11 flex items-center justify-center rounded-sm font-mono text-xs"
                               style={{ background: c.bg, border: `1.5px solid ${c.ring}` }}
                               data-testid={`cell-${t.type}-${s.time}`}>
                            <span style={{ color: c.ring }}>{s.available}</span>
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="overflow-x-auto lux-card p-4" data-testid="capacity-week">
          <table className="min-w-full">
            <thead>
              <tr>
                <th className="text-left text-[10px] uppercase tracking-widest text-[#a0b2af] pb-3 pr-3 sticky left-0 bg-[#062c27]">Tipologia</th>
                {(week?.days || []).map((d, i) => {
                  const dt = new Date(`${d.date}T12:00:00`);
                  const pad = n => String(n).padStart(2, "0");
                  return (
                    <th key={d.date} className="text-[10px] text-[#a0b2af] pb-3 px-2 text-center">
                      <div className="font-mono text-[#d4af37]">{WD[i]}</div>
                      <div className="mt-1">{pad(dt.getDate())}/{pad(dt.getMonth() + 1)}</div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody className="divide-y divide-[#1a4640]">
              {TYPES.map(t => (
                <tr key={t.type} className="hover:bg-[#0a3a33]/30" data-testid={`wrow-${t.type}`}>
                  <td className="py-2 pr-3 sticky left-0 bg-[#062c27]">
                    <div className="font-display text-base">{t.type}</div>
                    <div className="text-[10px] text-[#a0b2af] truncate max-w-[7rem]">{t.label}</div>
                  </td>
                  {(week?.days || []).map(d => {
                    const cell = d.by_type?.[t.type];
                    if (!cell) return <td key={d.date} className="p-0.5" />;
                    const c = loadColor(cell.load_pct);
                    return (
                      <td key={d.date} className="p-0.5">
                        <div title={`${d.date} · ${cell.available}/${cell.total} livres · ${cell.load_pct}% · ${c.label}`}
                             className="min-w-[54px] h-14 flex flex-col items-center justify-center rounded-sm"
                             style={{ background: c.bg, border: `1.5px solid ${c.ring}` }}
                             data-testid={`wcell-${t.type}-${d.date}`}>
                          <span className="font-mono text-sm" style={{ color: c.ring }}>{cell.available}</span>
                          <span className="text-[9px] text-[#a0b2af] mt-0.5">{cell.load_pct}%</span>
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
          {week && (
            <div className="mt-4 flex items-center gap-3 text-xs">
              <button type="button" onClick={() => setDate(addDays(weekStart(date), -7))}
                      className="btn-ghost-gold px-3 py-1.5 uppercase tracking-widest" data-testid="week-prev">← Semana anterior</button>
              <button type="button" onClick={() => setDate(addDays(weekStart(date), 7))}
                      className="btn-ghost-gold px-3 py-1.5 uppercase tracking-widest" data-testid="week-next">Semana seguinte →</button>
              <span className="text-[#a0b2af] ml-2">
                Slot de referência: <b className="text-[#fdfbf7]">{week.slot_time}</b>
              </span>
            </div>
          )}
        </div>
      )}

      <div className="flex items-center gap-2 text-xs text-[#a0b2af]">
        <Activity size={13} className="text-[#d4af37]" />
        {tab === "day"
          ? <>Cada célula mostra o <b className="text-[#fdfbf7]">número de colaboradores livres</b> para iniciar uma limpeza desse tipo à hora indicada.</>
          : <>Cada célula mostra os <b className="text-[#fdfbf7]">colaboradores livres às {week?.slot_time || "10:00"}</b> e a <b className="text-[#fdfbf7]">% de ocupação</b> do dia. Ideal para detectar padrões semanais.</>}
      </div>
      {loading && <div className="text-[#a0b2af] text-xs" data-testid="capacity-loading">A recalcular…</div>}
    </div>
  );
}
